import type { ReactNode } from 'react';
import { cx } from '../../lib/cx';
import { Container } from './Container';

/** Background treatments available to a section (spec section 11 palette). */
export type SectionTone = 'field' | 'warm' | 'ink';

const toneClasses: Record<SectionTone, string> = {
  field: 'bg-field text-ink',
  warm: 'bg-warm text-ink',
  ink: 'bg-ink text-field',
};

export interface SectionProps {
  id?: string;
  tone?: SectionTone;
  /** Vertical rhythm. `tight` is for sections that sit against a photo band. */
  spacing?: 'default' | 'tight';
  className?: string;
  /** Set when the section is introduced by a heading with this id. */
  labelledBy?: string;
  children: ReactNode;
}

/**
 * A full-width band of page content: background tone, vertical rhythm, and the
 * standard container. Pages are assembled from these rather than each page
 * inventing its own spacing.
 */
export function Section({
  id,
  tone = 'field',
  spacing = 'default',
  className,
  labelledBy,
  children,
}: SectionProps) {
  return (
    <section
      id={id}
      aria-labelledby={labelledBy}
      className={cx(
        'w-full',
        toneClasses[tone],
        spacing === 'tight' ? 'py-10 sm:py-14' : 'py-14 sm:py-20 lg:py-24',
        className,
      )}
    >
      <Container>{children}</Container>
    </section>
  );
}
